import React, { useEffect, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { signInWithEmailAndPassword, sendPasswordResetEmail, onAuthStateChanged } from 'firebase/auth'
import { auth, db } from '../firebase'
import { ref, get } from 'firebase/database'
import Popup from './Popup'
import Footer from './Footer'

// Modern minimal styles
const styles = {
  pageWrapper: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
  },
  container: {
    width: '100%',
    maxWidth: '680px',
  },
  card: {
    background: 'var(--card-bg, rgba(255, 255, 255, 0.95))',
    backdropFilter: 'blur(20px)',
    borderRadius: '24px',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    border: 'none',
    overflow: 'hidden',
  },
  cardBody: {
    padding: '48px 40px',
  },
  logoWrapper: {
    width: '80px',
    height: '80px',
    borderRadius: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 24px',
    boxShadow: '0 10px 30px rgba(102, 126, 234, 0.4)',
  },
  logoIcon: {
    fontSize: '2.5rem',
    color: '#fff',
  },
  title: {
    fontSize: '1.75rem',
    fontWeight: '700',
    color: 'var(--title-color, #1a1a2e)',
    marginBottom: '8px',
  },
  subtitle: {
    color: 'var(--subtitle-color, #6b7280)',
    fontSize: '0.95rem',
    marginBottom: '0',
  },
  formLabel: {
    fontSize: '0.875rem',
    fontWeight: '600',
    color: 'var(--label-color, #374151)',
    marginBottom: '8px',
  },
  inputWrapper: {
    position: 'relative',
  },
  formControl: {
    height: '52px',
    borderRadius: '12px',
    border: '2px solid var(--input-border, #e5e7eb)',
    paddingLeft: '48px',
    fontSize: '1rem',
    transition: 'all 0.2s ease',
    backgroundColor: 'var(--input-bg, #f9fafb)',
    color: 'var(--input-color, #1a1a2e)',
  },
  inputIcon: {
    position: 'absolute',
    left: '16px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: 'var(--icon-color, #9ca3af)',
    fontSize: '1.1rem',
    zIndex: 4,
  },
  eyeButton: {
    position: 'absolute',
    right: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    color: '#9ca3af',
    cursor: 'pointer',
    padding: '4px',
    zIndex: 4,
  },
  primaryBtn: {
    height: '52px',
    borderRadius: '12px',
    fontSize: '1rem',
    fontWeight: '600',
    border: 'none',
    boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
    transition: 'all 0.3s ease',
  },
  linkBtn: {
    color: '#667eea',
    fontWeight: '600',
    textDecoration: 'none',
    transition: 'color 0.2s ease',
  },
  errorAlert: {
    background: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '12px',
    color: '#dc2626',
    padding: '12px 16px',
    fontSize: '0.875rem',
  },
}

function Login() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [checkingAuth, setCheckingAuth] = useState(true)
  const [showResetModal, setShowResetModal] = useState(false)
  const [resetEmail, setResetEmail] = useState('')
  const [resetLoading, setResetLoading] = useState(false)
  const [resetMessage, setResetMessage] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [popup, setPopup] = useState({ isOpen: false, message: '', title: '', type: 'info' })
  const navigate = useNavigate()

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user && user.emailVerified) {
        try {
          const userRef = ref(db, `users/${user.uid}`)
          const snapshot = await get(userRef)

          if (snapshot.exists()) {
            const userData = snapshot.val()
            const role = userData.role

            if (role === "organizer") {
              navigate("/organizer", { replace: true })
            } else if (role === "bidder") {
              navigate("/bidder", { replace: true })
            } else {
              navigate("/", { replace: true })
            }
          }
        } catch (err) {
          console.error("Error fetching user data:", err)
        }
      }
      setCheckingAuth(false)
    })

    return () => unsubscribe()
  }, [navigate])

  const handleForgotPassword = () => {
    setShowResetModal(true)
    setResetEmail('')
    setResetMessage('')
  }

  const handleSendResetEmail = async () => {
    if (!resetEmail.trim()) {
      setResetMessage('Please enter your email address.')
      return
    }

    setResetLoading(true)
    setResetMessage('')

    try {
      await sendPasswordResetEmail(auth, resetEmail.trim())
      setResetMessage('Password reset email sent! Please check your inbox.')
      setTimeout(() => {
        setShowResetModal(false)
      }, 3000)
    } catch (error) {
      console.error('Reset email error:', error)
      switch (error.code) {
        case 'auth/invalid-email':
          setResetMessage('Invalid email address.')
          break
        case 'auth/user-not-found':
          setResetMessage('No account found with this email address.')
          break
        case 'auth/too-many-requests':
          setResetMessage('Too many requests. Please try again later.')
          break
        default:
          setResetMessage('Failed to send reset email. Please try again.')
      }
    } finally {
      setResetLoading(false)
    }
  }

  const handleCancelReset = () => {
    setShowResetModal(false)
    setResetEmail('')
    setResetMessage('')
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password)
      const user = userCredential.user
      if (!user.emailVerified) {
          await auth.signOut()
          setPopup({
            isOpen: true,
            title: 'Email Not Verified',
            message: 'Please verify your email address before accessing the dashboard. Check your inbox for the verification link.',
            type: 'warning'
          })
          setLoading(false)
          return
        }
      // Fetch user role from database
      const userRef = ref(db, `users/${user.uid}`)
      const snapshot = await get(userRef)

      if (snapshot.exists()) {
        const userData = snapshot.val()
        const role = userData.role

        // Redirect based on role
        if (role === 'organizer') {
          navigate('/organizer', { replace: true })
        } else if (role === 'bidder') {
          navigate('/bidder', { replace: true })
        } else {
          navigate('/', { replace: true })
        }
      } else {
        setError('User data not found. Please contact support.')
      }
    } catch (err) {
      console.error('Login error:', err)
      
      // Handle specific error codes
      switch (err.code) {
        case 'auth/invalid-email':
          setError('Invalid email address.')
          break
        case 'auth/user-disabled':
          setError('This account has been disabled.')
          break
        case 'auth/user-not-found':
          setError('No account found with this email.')
          break
        case 'auth/wrong-password':
          setError('Incorrect password.')
          break
        case 'auth/invalid-credential':
          setError('Invalid email or password.')
          break
        default:
          setError('Failed to sign in. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  // Show loading spinner while checking authentication
  if (checkingAuth) {
    return (
      <div style={styles.pageWrapper}>
        <div style={styles.container}>
          <div style={{ ...styles.card, padding: '60px', textAlign: 'center' }}>
            <div className="spinner-border" style={{ width: '3rem', height: '3rem', color: '#667eea' }} role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p style={{ marginTop: '16px', color: '#6b7280', marginBottom: 0 }}>Checking authentication...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
    <div style={styles.pageWrapper}>
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.cardBody}>
            {/* Logo & Header */}
            <div style={{ textAlign: 'center', marginBottom: '32px' }}>
              {/* <div style={styles.logoWrapper}>
                <i className="bi bi-trophy-fill" style={styles.logoIcon}></i>
              </div> */}
              <h1 style={styles.title}>Welcome !! </h1>
              <p style={styles.subtitle}>Sign in to SportsBid Arena</p>
            </div>

            {/* Error Alert */}
            {error && (
              <div style={styles.errorAlert} className="d-flex align-items-center mb-4">
                <i className="bi bi-exclamation-circle-fill me-2"></i>
                <span style={{ flex: 1 }}>{error}</span>
                <button
                  type="button"
                  style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer' }}
                  onClick={() => setError('')}
                >
                  <i className="bi bi-x-lg"></i>
                </button>
              </div>
            )}

            {/* Login Form */}
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '20px' }}>
                <label style={styles.formLabel}>Email Address</label>
                <div style={styles.inputWrapper}>
                  <i className="bi bi-envelope" style={styles.inputIcon}></i>
                  <input
                    type="email"
                    className="form-control"
                    style={styles.formControl}
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={loading}
                    autoComplete="email"
                  />
                </div>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={styles.formLabel}>Password</label>
                <div style={styles.inputWrapper}>
                  <i className="bi bi-lock" style={styles.inputIcon}></i>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="form-control"
                    style={{ ...styles.formControl, paddingRight: '48px' }}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={loading}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    style={styles.eyeButton}
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={loading}
                  >
                    <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                  </button>
                </div>
              </div>

              <button
                type="submit"
                className="btn w-100 btn-primary"
                style={styles.primaryBtn}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </button>
            </form>

            {/* Forgot Password */}
            <div style={{ textAlign: 'center', marginTop: '20px' }}>
              <button
                type="button"
                style={{ ...styles.linkBtn, background: 'none', border: 'none', cursor: 'pointer' }}
                onClick={handleForgotPassword}
                disabled={loading}
              >
                Forgot your password?
              </button>
            </div>

            {/* Divider */}
            <div style={{ display: 'flex', alignItems: 'center', margin: '28px 0' }}>
              <div style={{ flex: 1, height: '1px', background: '#e5e7eb' }}></div>
              <span style={{ padding: '0 16px', color: '#9ca3af', fontSize: '0.875rem' }}>or</span>
              <div style={{ flex: 1, height: '1px', background: '#e5e7eb' }}></div>
            </div>

            {/* Sign Up Link */}
            <div style={{ textAlign: 'center' }}>
              <p style={{ color: '#6b7280', marginBottom: 0 }}>
                Don't have an account?{' '}
                <Link to="/sign-up" style={styles.linkBtn}>
                  Create Account
                </Link>
              </p>
            </div>
          </div>
        </div>

        
      </div>

      {/* Forgot Password Modal */}
      {showResetModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '20px',
        }} onClick={handleCancelReset}>
          <div className="reset-modal-content" style={{
            background: 'var(--card-bg, #fff)',
            borderRadius: '24px',
            maxWidth: '420px',
            width: '100%',
            padding: '40px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <div style={{
                width: '64px',
                height: '64px',
                background: 'rgba(129, 140, 248, 0.3)',
                borderRadius: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
              }}>
                <i className="bi bi-envelope-paper" style={{ fontSize: '1.75rem', color: '#fff' }}></i>
              </div>
              <h4 style={{ fontWeight: '700', color: 'var(--title-color, #1a1a2e)', marginBottom: '8px' }}>Reset Password</h4>
              <p style={{ color: 'var(--subtitle-color, #6b7280)', fontSize: '0.95rem', marginBottom: 0 }}>
                Enter your email and we'll send you a reset link
              </p>
            </div>
            
            {resetMessage && (
              <div style={{
                background: resetMessage.includes('sent') ? '#ecfdf5' : '#fef2f2',
                border: `1px solid ${resetMessage.includes('sent') ? '#a7f3d0' : '#fecaca'}`,
                borderRadius: '12px',
                color: resetMessage.includes('sent') ? '#059669' : '#dc2626',
                padding: '12px 16px',
                fontSize: '0.875rem',
                marginBottom: '20px',
              }}>
                <i className={`bi bi-${resetMessage.includes('sent') ? 'check-circle' : 'exclamation-circle'}-fill me-2`}></i>
                {resetMessage}
              </div>
            )}

            <div style={{ marginBottom: '24px' }}>
              <label style={styles.formLabel}>Email Address</label>
              <div style={styles.inputWrapper}>
                <i className="bi bi-envelope" style={styles.inputIcon}></i>
                <input
                  type="email"
                  className="form-control"
                  style={styles.formControl}
                  placeholder="Enter your email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  disabled={resetLoading}
                  autoFocus
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button 
                onClick={handleCancelReset}
                className="reset-cancel-btn"
                style={{
                  flex: 1,
                  height: '48px',
                  borderRadius: '12px',
                  border: '2px solid var(--input-border, #e5e7eb)',
                  background: 'var(--card-bg, #fff)',
                  color: 'var(--label-color, #374151)',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
                disabled={resetLoading}
              >
                Cancel
              </button>
              <button 
                onClick={handleSendResetEmail}
                style={{
                  flex: 1,
                  height: '48px',
                  borderRadius: '12px',
                  border: 'none',
                  color: '#fff',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
                className="btn btn-primary"
                disabled={resetLoading || !resetEmail.trim()}
              >
                {resetLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Sending...
                  </>
                ) : (
                  'Send Link'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <Popup
        isOpen={popup.isOpen}
        onClose={() => setPopup({ isOpen: false, message: '', title: '', type: 'info' })}
        message={popup.message}
        title={popup.title}
        type={popup.type}
      />

      <style>{`
        .form-control:focus {
          border-color: #667eea !important;
          box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1) !important;
          background-color: #fff !important;
        }
        .btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(102, 126, 234, 0.5) !important;
        }
        
        /* Dark Mode Styles */
        [data-theme="dark"] .form-control:focus {
          background-color: #2d2d44 !important;
        }
        
        [data-theme="dark"] .form-control::placeholder {
          color: #808080 !important;
          opacity: 1;
        }
        
        [data-theme="dark"] .form-control {
          color: #f1f5f9 !important;
        }
      `}</style>
      
    </div>
      </>
  )
}

export default Login